import { FC, useState } from 'react'
import { ApplicationBodyFooter, ApplicationNextButtonsContainer, ButtonWithMessageContainer } from '../defaultComponents'
import Button from '../../basic/Button'
import { useNavigate, useLocation } from 'react-router-dom'
import { RenewalData } from 'src/dtos/renewalData'
import { useRenewal } from 'src/context/renewal'
import { useAuth } from 'src/context/auth'
import { IoMdArrowForward } from 'react-icons/io'
import loaderWhiteImg from 'src/assets/loader-white.svg'
import flagImg from 'src/assets/applicationFormsImgs/flag_with_check.svg'
import warningImg from 'src/assets/warning.svg'
import { useErrors } from 'src/context/errors'

interface PrebuiltRenewalFooterProps {
  goBackRoute: string;
  nextRoute?: string;
  updatedApplication?: RenewalData;
  isFormValid?: boolean;
  isCompleteApplicationAvailable?: boolean;
  beforeSaveContinueClick?: () => void;
  disableSaveandContinue? : boolean;
}

const PrebuiltRenewalFooter: FC<PrebuiltRenewalFooterProps> = ({
  goBackRoute,
  nextRoute,
  updatedApplication,
  isFormValid,
  isCompleteApplicationAvailable,
  beforeSaveContinueClick,
  disableSaveandContinue
}) => {
  const navigate = useNavigate()
  const { search, pathname } = useLocation()
  const { saveRenewalData, saveRenewalDataForLater, renewalData } = useRenewal()
  const { clearToast } = useErrors()
  const { user } = useAuth()
  const [isCompletingApp, setIsCompletingApp] = useState(false)
  const [buttonsDisabled, setButtonsDisabled] = useState(false)
  const [isActionBeingTaken, setIsActionBeingTaken] = useState<string | undefined>(undefined)
  const [showSaveLaterMessage, setShowSaveLaterMessage] = useState('close-container content-hidden')
  const [showDuplicateQuotesMessage, setShowDuplicateQuotesMessage] = useState('close-container content-hidden')

  const reviewMode = search.includes('review')

  const reviewUrl = renewalData?.insuranceType && renewalData?.insuranceType === 'LAWYER' ?
    (search.includes('firm') ? '/customer/renew/review/firm' : search.includes('renewal') ? '/customer/renew/review/questions' : '/customer/renew/review/attorneys') :
    (search.includes('basic') ? '/customer/renew/judge/review/basic' : '/customer/renew/judge/review/questions')

  const correctSearchParams = nextRoute?.includes('review') ? '' : search

  const goBack = () => {
    clearToast()
    navigate({
      pathname: goBackRoute,
      search
    })
  }

  const saveLaterClick = async () => {
    setIsActionBeingTaken('SAVE_LATER')
    setButtonsDisabled(true)
    try {
      await saveRenewalDataForLater({
        ...updatedApplication,
        stoppedAt: reviewMode ? reviewUrl : pathname
      })
      setShowSaveLaterMessage('open-container')
    } catch (_) {} finally {
      setIsActionBeingTaken(undefined)
      setButtonsDisabled(false)
    }
  }

  const saveContinueClick = async () => {
    beforeSaveContinueClick && beforeSaveContinueClick()
    if(isFormValid === undefined || isFormValid === true) {
      setIsActionBeingTaken('SAVE_CONTINUE')
      setButtonsDisabled(true)
      try {

        var redirectUrl = await saveRenewalData({
          ...updatedApplication,
          stoppedAt: reviewMode ? reviewUrl : nextRoute
        })
        
        if(!!redirectUrl){
          navigate({
            pathname: redirectUrl,
            search
          })
        }else{
          navigate({
            pathname: nextRoute,
            search: correctSearchParams
          })
        }
       
      } catch(_) {} finally {
        setIsActionBeingTaken(undefined)
        setButtonsDisabled(false)
      }
    } else {
      if(pathname.includes('/quote/select')) {
        setShowDuplicateQuotesMessage('open-container')
      }
    }
  }

  const returnToReview = async () => {
    if(isFormValid === undefined || isFormValid === true) {
      setIsActionBeingTaken('SAVE_REVIEW')
      setButtonsDisabled(true)
      try {
        await saveRenewalData({
          ...updatedApplication,
          stoppedAt: reviewMode ? reviewUrl : nextRoute
        })
        navigate(reviewUrl)
      } catch(_) {} finally {
        setIsActionBeingTaken(undefined)
        setButtonsDisabled(false)
      }
    }
  }

  const handleCompleteApplication = async () => {
    if(isFormValid === undefined || isFormValid === true) {
      setIsCompletingApp(true)
      try {
        await saveRenewalData({
          ...updatedApplication,
          stoppedAt: reviewMode ? reviewUrl : renewalData?.insuranceType && renewalData?.insuranceType === 'LAWYER' ?
            '/customer/renew/sign/complete' :
            '/customer/renew/judge/sign/complete'
        })

        navigate(
          renewalData?.insuranceType && renewalData?.insuranceType === 'LAWYER' ?
            '/customer/renew/sign/complete' :
            '/customer/renew/judge/sign/complete'
        )
      } catch(_) {} finally {
        setIsCompletingApp(false)
      }
    }
  }

  const handleDismissSaveLaterMessage = () => {
    setShowSaveLaterMessage('close-container')
    setTimeout(() => {
      setShowSaveLaterMessage('close-container content-hidden')
    }, 300)
  }

  const handleDismissDuplicateQuotes = () => {
    setShowDuplicateQuotesMessage('close-container')
    setTimeout(() => {
      setShowDuplicateQuotesMessage('close-container content-hidden')
    }, 300)
    beforeSaveContinueClick && beforeSaveContinueClick()
  }

  const completeAppButtonDisabled = !isFormValid || isCompletingApp

  return (
    <>
      <ApplicationBodyFooter>
        <Button prebuilt='back' onClick={goBack} disabled={buttonsDisabled}/>
        <ApplicationNextButtonsContainer>
          <ButtonWithMessageContainer>
            <div className={`savelater_message_container ${showSaveLaterMessage}`}>
              <div>
                <img src={flagImg} alt="flag" />
              </div>
              <div>
                <h5>Your application has been successfully saved.</h5>
                <p>
                  A link to continue your application will be sent to <strong>{user.email}.</strong>
                </p>
                <p>You can access and complete your application within 30 days. After that time, you can start a new application to continue.</p>
                <p>You can safely close this browser window.</p>
                <Button
                  variant="secondary"
                  padding='8px 12px'
                  minWidth='fit-content'
                  style={{ width: 'fit-content', marginLeft: 'auto' }}
                  onClick={handleDismissSaveLaterMessage}
                >
                  Dismiss
                </Button>
              </div>
            </div>
            <Button
              prebuilt='saveLater'
              disabled={buttonsDisabled}
              onClick={saveLaterClick}
              isTakingAction={isActionBeingTaken === 'SAVE_LATER'}
              data-save-later
            />
          </ButtonWithMessageContainer>
          {reviewMode ? (
            <Button
              onClick={returnToReview}
              disabled={buttonsDisabled}
              isTakingAction={isActionBeingTaken === 'SAVE_REVIEW'}
            >
              {isActionBeingTaken === 'SAVE_REVIEW' ? <img src={loaderWhiteImg} alt="loading"/> : 'Return to Review'}
            </Button>
          ) : null}
          {!!nextRoute ? (
            <ButtonWithMessageContainer>
            <div className={`savelater_message_container ${showDuplicateQuotesMessage}`}>
              <div>
                <img src={warningImg} alt="flag" />
              </div>
              <div>
                <h5>You’ve selected duplicate quotes</h5>
                <p>
                  You cannot submit multiple quotes with the same policy type, limit and deductible.
                </p>
                <Button
                  variant="secondary"
                  padding='8px 12px'
                  minWidth='fit-content'
                  style={{ width: 'fit-content', marginLeft: 'auto' }}
                  onClick={handleDismissDuplicateQuotes}
                >
                  Dismiss
                </Button>
              </div>
            </div>
            <Button
              variant={reviewMode ? 'plain' : 'primary'}
              disabled={buttonsDisabled || disableSaveandContinue}
              onClick={saveContinueClick}
              isTakingAction={isActionBeingTaken === 'SAVE_CONTINUE'}
              style={{ width: '220px', color: reviewMode ? '#3D6666' : '#FFF' }}
              data-save-continue
            >
              {isActionBeingTaken === 'SAVE_CONTINUE' ? <img src={loaderWhiteImg} alt="loading"/> : 'Save & Continue'}
              {isActionBeingTaken === 'SAVE_CONTINUE' ? null : <IoMdArrowForward style={{ marginLeft: '5px' }} />}
            </Button>
          </ButtonWithMessageContainer>
          ) : null}
          {!!isCompleteApplicationAvailable ? (
            <Button
              variant="primary"
              disabled={completeAppButtonDisabled}
              onClick={handleCompleteApplication}
            >
              {isCompletingApp ? <img src={loaderWhiteImg} alt="loading"/> : 'Complete renewal'}
              {isCompletingApp ? null : <IoMdArrowForward style={{ marginLeft: '5px' }} />}
            </Button>
          ) : null}
        </ApplicationNextButtonsContainer>
      </ApplicationBodyFooter>
    </>
  )
}

export default PrebuiltRenewalFooter